// Shared, velocity-aware proximity tracking.
//
// One scroll listener and a handful of IntersectionObservers serve the whole
// site: every registered element gets a "near viewport" callback whose
// lookahead band grows with scroll speed, so a fling warms far more content
// than an idle read does. Things behind the viewport are never promoted.

type Entry = {
  el: Element;
  base: number;
  cb: () => void;
};

const entries = new Map<Element, Entry>();
const observers = new Map<number, IntersectionObserver>();

let velocity = 0;
let tier = 1;
let started = false;

/** Multiplier applied to each element's base band, by velocity tier. */
const TIER_SCALE = [1, 1, 2.2, 4, 5.5];

function currentTier(v: number): number {
  if (v > 3000) return 4;
  if (v > 1500) return 3;
  if (v > 500) return 2;
  return 1;
}

function bandFor(base: number): number {
  return Math.round(base * TIER_SCALE[tier]);
}

function observerFor(base: number): IntersectionObserver {
  const existing = observers.get(base);
  if (existing) return existing;
  const io = new IntersectionObserver(
    (records) => {
      for (const r of records) {
        if (!r.isIntersecting) continue;
        const entry = entries.get(r.target);
        if (!entry) continue;
        unobserve(r.target);
        entry.cb();
      }
    },
    { rootMargin: `${bandFor(base)}px 0px` },
  );
  observers.set(base, io);
  return io;
}

function rebuild() {
  for (const io of observers.values()) io.disconnect();
  observers.clear();
  for (const entry of entries.values()) observerFor(entry.base).observe(entry.el);
}

function startTracking() {
  if (started || typeof window === "undefined") return;
  started = true;

  let lastY = window.scrollY;
  let lastT = performance.now();
  let ticking = false;
  let decay: ReturnType<typeof setTimeout> | null = null;

  const sample = () => {
    ticking = false;
    const y = window.scrollY;
    const t = performance.now();
    const dt = t - lastT;
    if (dt > 8) {
      const v = (Math.abs(y - lastY) / dt) * 1000;
      // Smoothed so one jittery frame can't spike the lookahead.
      velocity = velocity * 0.6 + v * 0.4;
      lastY = y;
      lastT = t;
    }
    const next = currentTier(velocity);
    if (next !== tier) {
      tier = next;
      rebuild();
    }
    if (decay) clearTimeout(decay);
    decay = setTimeout(() => {
      velocity = 0;
      // Never shrink the band back down mid-session: content already promoted
      // stays promoted, and a calmer tier only affects future registrations.
      tier = 1;
    }, 200);
  };

  window.addEventListener(
    "scroll",
    () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(sample);
    },
    { passive: true },
  );
}

export function scrollVelocity(): number {
  return velocity;
}

function unobserve(el: Element) {
  const entry = entries.get(el);
  if (!entry) return;
  entries.delete(el);
  observers.get(entry.base)?.unobserve(el);
}

/**
 * Fire `cb` once when `el` comes within the (velocity-scaled) band ahead of
 * the viewport. Returns an unsubscribe function. Falls back to firing
 * immediately where IntersectionObserver is unavailable.
 */
export function observeNear(el: Element, base: number, cb: () => void): () => void {
  if (typeof IntersectionObserver === "undefined") {
    cb();
    return () => undefined;
  }
  startTracking();
  entries.set(el, { el, base, cb });
  observerFor(base).observe(el);
  return () => unobserve(el);
}

/** Run work off the interaction path. */
export function onIdle(cb: () => void, timeout = 400) {
  if (typeof window === "undefined") return;
  const ric = (
    window as Window & {
      requestIdleCallback?: (cb: IdleRequestCallback, o?: { timeout: number }) => number;
    }
  ).requestIdleCallback;
  if (ric) ric(() => cb(), { timeout });
  else setTimeout(cb, 24);
}
